import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import type { Currency } from "@przezmorze/api-types";
import noop from "lodash/noop";
import { useEffect, useState } from "react";
import { usePackages } from "../../admin/packages/usePackages";
import { isPlaneTicket } from "../../admin/providers/isPlaneTicket";
import type { Package } from "../../sharedComponents/packages/PackageSelect";
import PackageSelect from "../../sharedComponents/packages/PackageSelect";
import type { Product } from "../../sharedComponents/products/ProductSelect";
import type { ProductFilterFn } from "../components/ProductSelectAction";
import { ProductSelectAction } from "../components/ProductSelectAction";
import { RadioSelect } from "../generic/RadioSelect";
import { ToggleButtonSelect } from "../generic/ToggleButtonSelect";

const modeOptions = {
  package: "Kalkulacja na podstawie pakietu",
  product: "Kalkulacja własna z produktów",
} as const;
type SelectionMode = keyof typeof modeOptions;

const sourceOptions = {
  products: "Produkty",
  flights: "Bilety lotnicze",
  ao: "A&O",
} as const;
type SourceMode = keyof typeof sourceOptions;

const placeholders: Record<SourceMode, string> = {
  products: "Zacznij pisać, aby wyszukać produkt...",
  flights: "Zacznij pisać, aby wyszukać loty...",
  ao: "Zacznij pisać, aby wyszukać A&O...",
};

const mapSelectionAvailable: Record<SourceMode, boolean> = {
  products: true,
  flights: false,
  ao: true,
};
const productTypeFilter: Record<SourceMode, ProductFilterFn> = {
  products: () => true,
  flights: (p): boolean => p.provider.productType != null && isPlaneTicket(p.provider.productType),
  ao: (p): boolean => !!p.provider.name?.toUpperCase().includes("A&O"),
};

export function PackageOrProductSection(props: {
  packageId?: string;
  onPackageSelected(v: Package): void;
  onProductSelected(v: Product): void;
  onModeChanged(v: SelectionMode): void;
  readonly: boolean;
  currency?: Currency;
}) {
  const [mode, setMode] = useState<SelectionMode>(getMode(props.packageId));
  const [source, setSource] = useState<SourceMode>("products");
  const { packages } = usePackages();
  useEffect(() => {
    setMode(getMode(props.packageId));
  }, [props.packageId]);
  const packageInfo = packages?.find((p) => p.id.id === props.packageId)?.note || undefined;
  const readonly = props.readonly;

  if (readonly) {
    return null;
  }
  return (
    <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 1, border: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          mb: 1,
        }}
      >
        Wybierz pakiety lub produkty
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3,
        }}
      >
        Wybierz główny tryb kalkulacji, aby odblokować odpowiednie możliwości.
      </Typography>
      <RadioSelect
        options={modeOptions}
        value={mode}
        onChange={(v) => {
          props.onModeChanged(v);
          return setMode(v);
        }}
        disabled={readonly}
      />
      <Stack direction="column" spacing={3} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 1 }}>
          <ToggleButtonSelect
            options={{ package: "Baza pakietów" }}
            value="package"
            onChange={noop}
            disabled={mode !== "package" || readonly}
          />
          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" }, mx: 1 }} />

          <ToggleButtonSelect
            disabled={mode !== "product" || readonly}
            options={sourceOptions}
            value={source}
            onChange={(v) => setSource(v)}
          />
        </Stack>

        <Box sx={{ width: "100%", maxWidth: 850 }}>
          {mode === "product" && (
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              <ProductSelectAction
                onProductSelected={props.onProductSelected}
                placeholder={placeholders[source]}
                filter={productTypeFilter[source]}
                mapSelectionAvailable={mapSelectionAvailable[source]}
                disabled={readonly}
                currency={props.currency}
              />
            </Stack>
          )}
          {mode === "package" && (
            <>
              <PackageSelect
                packageId={props.packageId ?? ""}
                onChange={(v) => v && props.onPackageSelected(v)}
                label="Wybierz pakiet źródłowy"
                disabled={readonly}
              />
              {packageInfo && (
                <Box
                  sx={{
                    p: 2,
                    mt: 3,
                    bgcolor: "rgba(13, 110, 253, 0.04)",
                    borderRadius: 1,
                    border: "1px solid rgba(13, 110, 253, 0.1)",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "flex-start",
                    }}
                  >
                    <InfoOutlinedIcon color="primary" fontSize="small" sx={{ mt: 0.3 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      <strong>Informacja z pakietu:</strong> {packageInfo}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
function getMode(packageId?: string) {
  return packageId ? "package" : "product";
}
